import { CalendarIcon, ClockIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/hooks";
import { Clock11, Tag } from "lucide-react";
import { FC, useState } from "react";
import { cancelSubscription } from "redux/features/subscription/subscriptionSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { EASING } from "../../../utils/easingUtils";

export const CancelSubscriptionModal: FC<{
  open: boolean;
  subscription: any | null;
  handleClose: () => void;
}> = ({ open, subscription, handleClose }) => {
  const [cancelOption, setCancelOption] = useState<"immediate" | "end_of_period" | "custom_date">("immediate");
  const [customDate, setCustomDate] = useState<string>("");
  const dispatch = useAppDispatch();
  const handleCancel = () => {
    dispatch(
      cancelSubscription({
        userId: parseInt(subscription.userId),
        subscriptionId: subscription?.id,
        option: cancelOption,
        customDate: cancelOption === "custom_date" ? customDate : undefined,
      })
    );

    handleClose();
  };

  if (!open || !subscription) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
          role="dialog"
          aria-labelledby="modal-title"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: EASING.easeOut }}
            className="w-full max-w-md p-6 bg-white shadow-2xl dark:bg-gray-900 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                id="modal-title"
                className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white"
              >
                <Tag className="w-6 h-6 text-blue-500" />
                Annuler l'abonnement
              </h3>
              <button
                onClick={handleClose}
                className="p-1 text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                aria-label="Fermer la fenêtre modale"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-5">
              <motion.div
                className="flex items-center gap-3 p-3 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <input
                  type="radio"
                  name="cancelOption"
                  value="immediate"
                  checked={cancelOption === "immediate"}
                  onChange={() => setCancelOption("immediate")}
                  className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  id="immediate"
                />
                <label
                  htmlFor="immediate"
                  className="flex items-center gap-2 text-gray-900 cursor-pointer dark:text-gray-100"
                >
                  <Clock11 className="w-5 h-5 text-blue-400" />
                  Immédiatement
                </label>
              </motion.div>
              <motion.div
                className="flex items-center gap-3 p-3 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <input
                  type="radio"
                  name="cancelOption"
                  value="end_of_period"
                  checked={cancelOption === "end_of_period"}
                  onChange={() => setCancelOption("end_of_period")}
                  className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  id="end_of_period"
                />
                <label
                  htmlFor="end_of_period"
                  className="flex items-center gap-2 text-gray-900 cursor-pointer dark:text-gray-100"
                >
                  <ClockIcon className="w-5 h-5 text-blue-400" />À la fin de la période d'essai
                </label>
              </motion.div>
              <motion.div
                className="flex flex-col gap-3 p-3 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="cancelOption"
                    value="custom_date"
                    checked={cancelOption === "custom_date"}
                    onChange={() => setCancelOption("custom_date")}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    id="custom_date"
                  />
                  <label
                    htmlFor="custom_date"
                    className="flex items-center gap-2 text-gray-900 cursor-pointer dark:text-gray-100"
                  >
                    <CalendarIcon className="w-5 h-5 text-blue-400" />À une date personnalisée
                  </label>
                </div>
                <AnimatePresence>
                  {cancelOption === "custom_date" && (
                    <motion.input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="w-full p-3 mt-2 text-gray-900 transition-all border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                      min={new Date().toISOString().split("T")[0]}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <ButtonSecondary
                onClick={handleClose}
                className="px-5 py-2.5 text-gray-700 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Annuler
              </ButtonSecondary>
              <ButtonPrimary
                onClick={handleCancel}
                className="px-5 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirmer
              </ButtonPrimary>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
